/**
 * The layerhost acts at the gateway between the client application and the DXL layer. It&#x27;s sole responsibilities are providing data context to it&#x27;s children as well as exposing methods for sending and receiving data to the DXL
 *
 * @module views/components/LayerHost
 * @memberof -Common
 */
import React, { useState, useEffect, useRef } from 'react';

import { constants } from '../../../utils/constants/constants';
import { useDXLQuery } from '../../../utils/dxl/dxl';
import { useLayerHostContext } from '../LayerHostProvider/LayerHostProvider';
import { useUserContext } from '../UserContextProvider/UserContextProvider';
import { useLayerHostSkip } from '../../../utils/useLayerHostSkip/useLayerHostSkip';
import AsyncComponent from '../AsyncComponent/AsyncComponent';
import { usePageDataContext } from '../PageDataProvider/PageDataProvider';
import useLoader from '../../../utils/useLoader/useLoader';
import './LayerHost.scss';
import { useDeviceInflection } from '../InflectionProvider/InflectionProvider';
import { useRetailerVisitorId } from '../../../utils/useRetailerVisitorId/useRetailerVisitorId';
const { COLUMN_CONTAINER_BREAKPOINTS } = require( '../../../utils/constants/constants' ).default;

// export const handleDataCapture = function( methods, data ){
//   return function( datacapture ){
//   }
// }

/**
 * Represents a LayerHost component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns LayerHost
 */
export const LayerHost = function( props ){
  const {
    broadcastModuleUpdate,
    clearBroadcastModuleUpdate,
    refreshContentId,
    refreshAction,
    refreshData
  } = useLayerHostContext();
  // this will be used to resolve data keys from an action. Not yet implmented, but all GTI and username type data
  // will be resolved in this layer instead of each component needing to do so
  const { setPageData } = usePageDataContext();
  const user = useUserContext();
  const { retailerVisitorId } = useRetailerVisitorId();
  const moduleRef = useRef();
  const [viewData, setViewData] = useState( null );
  const [invokeQuery, setInvokeQuery] = useState( null );
  const [invokeMutation, setInvokeMutation] = useState( null );
  const [moduleContentId, setModuleContentId] = useState( null );
  const [data, setData] = useState( null );
  const [isQuery, setIsQuery] = useState( false );
  const [loading, setLoading] = useState( null );
  const [error, setError] = useState( null );
  const { breakpoint } = useDeviceInflection();
  const CURRENT_BREAKPOINT = COLUMN_CONTAINER_BREAKPOINTS[breakpoint.CURRENT_BREAKPOINT];
  const [elementWidth, setElementWidth] = useState( null );
  const [displayAsMobile, setDisplayAsMobile] = useState( null );
  const [containerQuery, setContainerQuery] = useState( null );
  const environment = breakpoint.CURRENT_BREAKPOINT === 'SM' ? 'm' : 'd';
  const [skip, mutation, query] = useLayerHostSkip(
    {
      broadcastModuleUpdate,
      clearBroadcastModuleUpdate,
      setData
    },
    {
      user,
      retailerVisitorId,
      environment,
      id: props.id,
      invokeQuery,
      invokeMutation,
      refreshAction,
      refreshContentId,
      refreshData,
      meta: props.meta,
      containerLayerHostContentId: props.containerLayerHostContentId
    }
  );

  let {
    loading: queryLoading,
    data: queryData,
    error: queryError
  } = useDXLQuery( query.graphql, {
    variables: query.variables,
    skip
  } );
  let [actionMutation, { loading: mutationLoading, data: mutationData, error: mutationError }] =
    useDXLQuery( mutation.graphql );

  useEffect( () => {
    handleRefreshData( refreshData, { setPageData } );
  }, [refreshData] );

  useEffect( () => {
    handleActionMutation(
      { mutation, CURRENT_BREAKPOINT },
      { user, actionMutation, setIsQuery, setInvokeMutation }
    );
  }, [mutation.graphql, mutation?.variables, user?.loginType] );

  useEffect( () => {
    handleLayerHostUpdate(
      { props, mutationLoading, mutationData, mutationError },
      { setLoading, setData, setError, broadcastModuleUpdate }
    );
  }, [mutationLoading, mutationData, mutationError] );

  useEffect( () => {
    handleSetMutationResponses(
      { queryLoading, queryData, queryError },
      { setLoading, setData, setError, setIsQuery }
    );
  }, [queryLoading, queryData, queryError] );

  const [loader, showLoader] = useLoader( { color: 'orange-400' } );

  useEffect( () => {
    // TODO if the query doesn't have ac ontentId we should make a query with the provided contentID
    // TODO the contentID in dxl right now is not a param, but in the query, os we need to address that to easily get access to it
    if( data ){
      const dxlDataKey = 'Page';
      const responseData = data[dxlDataKey].content;
      if( responseData ){
        setViewData( { ...responseData } );
        setModuleContentId( responseData.id );
      }
    }
    showLoader();
  }, [data] );

  // TODO Follow up with Sreekala around this datacapture implemtnation for centralizing
  // these calls
  // const isIntersecting = useIntersectionObserver( moduleRef, {
  //   root: root,
  //   rootMargin: rootMargin,
  //   threshold: threshold
  // }, handleIntersection( props, moduleRef ) )

  // const invokeDataCapture = handleDataCapture( {}, {} );

  const { Content } = props;
  // components which do not require Layerhost metadata and datacapture
  const declineList = ['WebpageMetaData', 'DataCaptureEvent'];
  // As the placement of viewData and props in content module one above the other were causing overriding of data
  //  fetched from Query and Mutation calls. Destructured both into an object and passing it to the content
  const modifiedData = props.id && isQuery ? { ...props, ...viewData } : { ...viewData, ...props };

  // sets the child component to display its mobile inflection if
  // configureContainerQuery is called and maxWidth is set
  // from the child component
  useEffect( () => {
    if( containerQuery && moduleRef.current ){
      global.addEventListener( 'resize', () =>
        handleElementWidth( { moduleRef: moduleRef.current }, { setElementWidth } )
      );
      if( !elementWidth ){
        handleElementWidth( { moduleRef: moduleRef.current }, { setElementWidth } );
      }
    }
    return () => {
      global.removeEventListener( 'resize', () =>
        handleElementWidth( { moduleRef: moduleRef.current }, { setElementWidth } )
      );
    };
  }, [containerQuery, moduleRef.current] );

  useEffect( () => {
    if( elementWidth && containerQuery?.maxWidth ){
      // console.log( { containerQuery, elementWidth } )
      handleSetDisplayAsMobile(
        { elementWidth, maxWidth: containerQuery.maxWidth, displayAsMobile },
        { setDisplayAsMobile }
      );
    }
  }, [containerQuery, elementWidth] );

  return (
    <>
      { declineList.includes( props.moduleName ) && <Content { ...props } /> }
      { props.id && (
        <Content
          ref={ moduleRef }
          invokeAction={ setInvokeQuery }
          invokeMutation={ setInvokeMutation }
          configureContainerQuery={ configureContainerQuery(
            { containerQuery },
            { setContainerQuery }
          ) }
          // invokeDataCapture={ invokeDataCapture }
          containerLayerHostContentId={ moduleContentId || props.containerLayerHostContentId }
          loading={ loading }
          error={ error }
          { ...( containerQuery?.maxWidth && { displayAsMobile } ) }
          { ...modifiedData }
        />
      ) }
      { !props.id && viewData && (
        <AsyncComponent
          ref={ moduleRef }
          containerLayerHostContentId={ moduleContentId || props.containerLayerHostContentId }
          configureContainerQuery={ configureContainerQuery(
            { containerQuery },
            { setContainerQuery }
          ) }
          loading
          error
          { ...( containerQuery?.maxWidth && { displayAsMobile } ) }
          { ...viewData }
        />
      ) }
      { !props.id && loading && <div className='Layerhost__Progress'>{ loader }</div> }
    </>
  );
};

/**
 * Configures the container query
 *
 * @method
 * @param {object} methods
 * @param {object} data
 */
export const configureContainerQuery = ( data, methods ) => {
  return ( config ) => {
    if( config !== data.containerQuery ){
      methods.setContainerQuery( config );
    }
  };
};

/**
 * Sets element width
 *
 * @method
 * @param {object} methods
 * @param {object} data
 */
export const handleElementWidth = ( data, methods ) => {
  const { moduleRef } = data;
  const { setElementWidth } = methods;
  setElementWidth( moduleRef.offsetWidth );
};

/**
 * Sets dispayAsMobile to true or false
 *
 * @method
 * @param {object} methods
 * @param {object} data
 */
export const handleSetDisplayAsMobile = ( data, methods ) => {
  const { elementWidth, maxWidth, displayAsMobile } = data;
  const { setDisplayAsMobile } = methods;
  if( elementWidth < maxWidth && !displayAsMobile ){
    setDisplayAsMobile( true );
  }
  if( elementWidth > maxWidth && displayAsMobile ){
    setDisplayAsMobile( false );
  }
};

export const handleActionMutation = ( data, methods ) => {
  const { actionMutation, setInvokeMutation, setIsQuery } = methods;
  const { mutation, CURRENT_BREAKPOINT } = data;
  if( mutation.graphql && mutation.variables && Object.entries( mutation.variables )?.length > 0 ){
    actionMutation( {
      variables: {
        ...mutation.variables,
        moduleParams: {
          ...mutation.variables.moduleParams,
          inflectionType: CURRENT_BREAKPOINT,
          loginStatus: methods.user?.loginType
        }
      }
    } );
    setIsQuery( false );
    setInvokeMutation( null );
  }
};

export const handleSetMutationResponses = ( data, methods ) => {
  const { queryLoading, queryData, queryError } = data;
  const { setLoading, setData, setError, setIsQuery } = methods;
  if( queryLoading || queryData || queryError ){
    setLoading( queryLoading );
    setError( queryError );
    setData( queryData );
    setIsQuery( true );
  }
};

export const handleLayerHostUpdate = ( data, methods ) => {
  const { props, mutationLoading, mutationData, mutationError } = data;
  const { setLoading, setData, setError, broadcastModuleUpdate } = methods;
  if( mutationLoading || mutationData || mutationError ){
    setLoading( mutationLoading );
    setError( mutationError );
    if( mutationData ){
      // the mutation response isn't guaranteed to be an exact match for this level of the layer host instance,
      // so we need to do another comparison check like we do on the query side, but then broadcast a "setLayerHostData", which
      // will use the moduleId as one argument along with the data from the mutation as the second to update the layer host instance
      if( mutationData.id !== props.id ){
        // broadcast the refresh with content
        broadcastModuleUpdate( undefined, {
          contentId: mutationData.id,
          data: mutationData
        } );
        setData( mutationData );
      }
      else {
        setData( mutationData );
      }
    }
  }
};

export const handleRefreshData = ( refreshData, methods ) => {
  if( refreshData ){
    methods.setPageData( { loading: false, data: refreshData, error: '' } );
  }
};

/**
 * Default values for passed properties
 * @type {object}
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

LayerHost.defaultProps = defaultProps;

export default LayerHost;
